import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { fetchSoundtrack } from '../../infrastructure/content/soundtrackClient';
import { playOrder, readSoundtrack, stepOrder } from '../../domain/sound/soundtrack';
import { useTranslate } from '../i18n/LocaleProvider';
import { bindMediaSessionActions, publishNowPlaying } from './mediaSession';
import {
    readJukeboxShuffle,
    readJukeboxTrackId,
    writeJukeboxShuffle,
    writeJukeboxTrackId,
} from './jukeboxPreferences';

/**
 * The jukebox: one `<audio>` element that outlives every screen (SPEC-014
 * PR-14.2).
 *
 * AN ELEMENT, NOT WEBAUDIO, and that is the whole reason this does not live in
 * `duelSounds.js` beside the clock. A tick is seven kilobytes that has to fire
 * on an exact frame, so it is fetched whole, decoded into an `AudioBuffer` and
 * fired from a buffer source. A track is three to seven megabytes; the same
 * treatment would download twenty-four minutes of music into a phone's heap to
 * play three of it, and would still be worse at the job — an `<audio>` element
 * streams with range requests, starts on the first few kilobytes, survives a
 * backgrounded tab, and is the only thing on iOS that reaches the lock screen.
 * Two mechanisms, because there are two problems.
 *
 * THE ELEMENT IS RENDERED HERE, ABOVE THE ROUTES, so that navigating does not
 * stop the music. An element mounted by the jukebox SCREEN would be destroyed
 * with it the moment the reader walked to the garage, which is the one thing a
 * jukebox may not do.
 *
 * THE TRANSPORT IS IMPERATIVE, NOT AN EFFECT, and that is load-bearing rather
 * than a style choice. iOS Safari will only start a media element inside a real
 * user gesture, and a gesture ends when the handler returns — so an effect that
 * set `src` and called `play()` after a state change would be a play() outside
 * the gesture that asked for it, which is refused. Every function below that
 * starts a sound does its work in the handler's own call stack; state follows.
 *
 * THE SHELF IS FETCHED ON DEMAND rather than at boot. It is two kilobytes, but
 * it is two kilobytes and a request on the critical path of every reader who
 * never presses the tab.
 */

const JukeboxContext = createContext(null);

/**
 * `timeupdate` fires about four times a second and this context has consumers on
 * screen wherever the reader is, so a naive `setElapsed` per event would re-render
 * the tab bar two hundred and forty times a minute for a progress bar that moves
 * under two pixels a second. Whole seconds is what a clock shows anyway.
 */
function toWholeSecond(time) {
    return Number.isFinite(time) ? Math.floor(time) : 0;
}

export function JukeboxProvider({ children }) {
    const t = useTranslate();
    const audioRef = useRef(null);
    // Mirrors `status` for the one decision that must not re-create `loadShelf`.
    // See the note on it.
    const loadState = useRef('idle');
    const [tracks, setTracks] = useState([]);
    const [status, setStatus] = useState('idle');
    const [index, setIndex] = useState(0);
    /* The position, readable without depending on it. The order is dealt FROM
       it and must not be re-dealt BY it — see the memo below. */
    const indexRef = useRef(0);
    const [isPlaying, setPlaying] = useState(false);
    const [isShuffled, setShuffled] = useState(readJukeboxShuffle);
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(0);
    // Re-drawn every time shuffle is switched on, so a reader who does not like
    // the order they were given can ask for another one by turning it off and
    // on. Held in state rather than in a ref because the order is derived from
    // it and a ref would not re-derive.
    const [seed, setSeed] = useState(1);

    /* Which rows are versions of one another, positionally. The shuffle needs
       it to keep three takes of `4.1 Seconds` from landing in a row; the shelf
       and the transport do not care. */
    const families = useMemo(() => tracks.map((track) => track.family), [tracks]);

    /**
     * THE RUNNING ORDER IS DEALT ONCE AND THEN WALKED. It is deliberately NOT
     * recomputed when the position moves, and that omission is the whole of this
     * memo — `index` is read through a ref precisely so it cannot become a
     * dependency.
     *
     * It was a dependency for one revision and the jukebox was unusable. The
     * order holds the playing track first, so recomputing it on every step meant
     * `stepOrder` was never walking a list — it was asking "what would a good
     * next track be" over and over, against a fixed seed, and getting the same
     * answer. On the shipped shelf that is a TWO-CYCLE: pressing next walked
     * 3, 11, 3, 11 for ever, and eighteen of the twenty tracks could not be
     * reached at all. It also broke `previous`, which landed on whatever the
     * freshly dealt order happened to end with.
     *
     * So the order is dealt when the SHELF changes, when shuffle is switched, or
     * when a new seed is drawn — the three moments a new running order is
     * actually wanted — and every step after that walks the one already dealt.
     */
    const order = useMemo(
        () => playOrder({
            count: tracks.length, isShuffled, startAt: indexRef.current, seed, families,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see above: `index`
        // is read at deal time and must not re-deal.
        [tracks.length, isShuffled, seed, families],
    );

    /**
     * Pull the shelf in, once.
     *
     * GUARDED ON A REF, AND THE CALLBACK TAKES NO DEPENDENCIES, which is
     * load-bearing rather than a micro-optimisation. The screen calls this from
     * an effect keyed on the function itself, so a `loadShelf` that changed
     * identity whenever `status` did would re-fire that effect on every
     * transition — and since a failed load leaves a status that is neither
     * `loading` nor `ready`, the guard would let the retry through and the
     * screen would sit there flickering between "loading" and "try again" for
     * as long as the reader looked at it. A ref keeps the identity stable, so
     * the effect runs exactly once per mount.
     *
     * A FAILURE RETURNS THE REF TO `idle`, which is what keeps the retry button
     * meaningful: the common cause is a reader who opened the tab in a lift, and
     * pressing it has to actually try again.
     */
    const loadShelf = useCallback(async () => {
        if (loadState.current !== 'idle') {
            return;
        }

        loadState.current = 'loading';
        setStatus('loading');

        try {
            const shelf = readSoundtrack(await fetchSoundtrack());

            if (shelf.length === 0) {
                throw new Error('the soundtrack manifest carries no playable tracks');
            }

            setTracks(shelf);
            // Where this device left off, by id. An id that is no longer on the
            // shelf — a track dropped from the build — is not an error; it
            // simply does not match, and the jukebox opens on the first track.
            const remembered = shelf.findIndex((track) => track.id === readJukeboxTrackId());

            indexRef.current = remembered === -1 ? 0 : remembered;
            setIndex(indexRef.current);
            loadState.current = 'ready';
            setStatus('ready');
        } catch (error) {
            // A shelf that will not arrive is a jukebox with nothing on it. The
            // screen says so and offers the retry; nothing else in the app knows
            // or cares.
            loadState.current = 'idle';
            setStatus('failed');
        }
    }, []);

    /**
     * Put a track on and start it.
     *
     * The `src` is only rewritten when the track actually changes: assigning the
     * same address again reloads the element, which would restart the song a
     * reader pressed pause and then play on.
     */
    const playAt = useCallback((position) => {
        const audio = audioRef.current;
        const track = tracks[position];

        if (!audio || !track) {
            return;
        }

        if (audio.dataset.trackId !== track.id) {
            audio.src = track.src;
            audio.dataset.trackId = track.id;
            setElapsed(0);
            setDuration(0);
            writeJukeboxTrackId(track.id);
        }

        setIndex(position);


        try {
            const started = audio.play();

            if (started && typeof started.catch === 'function') {
                // Refused — no gesture behind it, or a platform that will not
                // start media at all. `onPause` below is not fired for a play
                // that never began, so the flag is cleared here.
                started.catch(() => setPlaying(false));
            }
        } catch (error) {
            // jsdom has no media pipeline and throws synchronously. So does a
            // browser with autoplay locked down. Neither is worth a screen.
            setPlaying(false);
        }
    }, [tracks]);

    const pause = useCallback(() => {
        const audio = audioRef.current;

        if (audio && typeof audio.pause === 'function') {
            audio.pause();
        }

        setPlaying(false);
    }, []);

    const toggle = useCallback(() => {
        if (isPlaying) {
            pause();
            return;
        }

        playAt(index);
    }, [isPlaying, pause, playAt, index]);

    /**
     * Along the ORDER rather than along the shelf, so skipping under shuffle
     * follows the shuffle. `stepOrder` wraps at both ends: this plays under an
     * app somebody is using for something else, and a shelf that went quiet
     * after twenty-four minutes would read as something they had broken.
     */
    const step = useCallback((by) => {
        const next = stepOrder(order, index, by);

        if (next !== -1) {
            playAt(next);
        }
    }, [order, index, playAt]);

    const toggleShuffle = useCallback(() => {
        const next = !isShuffled;

        setShuffled(next);
        writeJukeboxShuffle(next);

        if (next) {
            setSeed(Date.now() >>> 0);
        }
    }, [isShuffled]);

    const seekTo = useCallback((fraction) => {
        const audio = audioRef.current;

        if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) {
            return;
        }

        audio.currentTime = Math.min(1, Math.max(0, fraction)) * audio.duration;
        setElapsed(toWholeSecond(audio.currentTime));
    }, []);

    const track = tracks[index] || null;

    // The lock screen, Control Centre and the notification shade. Republished
    // whenever what is playing changes, because the OS keeps whatever it was
    // last told — including, otherwise, the title of a track that finished.
    useEffect(() => {
        publishNowPlaying({ track, isPlaying, albumTitle: t('jukebox.album') });
    }, [track, isPlaying, t]);

    useEffect(() => bindMediaSessionActions({
        play: () => playAt(index),
        pause,
        nexttrack: () => step(1),
        previoustrack: () => step(-1),
    }), [playAt, pause, step, index]);

    const value = useMemo(() => ({
        tracks,
        track,
        index,
        status,
        isPlaying,
        isShuffled,
        elapsed,
        duration,
        loadShelf,
        play: () => playAt(index),
        pause,
        toggle,
        next: () => step(1),
        previous: () => step(-1),
        selectTrack: playAt,
        toggleShuffle,
        seekTo,
    }), [
        tracks, track, index, status, isPlaying, isShuffled, elapsed, duration,
        loadShelf, playAt, pause, toggle, step, toggleShuffle, seekTo,
    ]);

    return (
        <JukeboxContext.Provider value={value}>
            {children}
            {/* `preload="none"`: a reader who never opens the panel must not
                fetch a note of this, and one who opens it and browses the shelf
                should not pull three megabytes down for a track they are only
                reading the name of. */}
            <audio
                ref={audioRef}
                preload="none"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => step(1)}
                onDurationChange={(event) => setDuration(toWholeSecond(event.target.duration))}
                onTimeUpdate={(event) => setElapsed((was) => {
                    const now = toWholeSecond(event.target.currentTime);

                    return now === was ? was : now;
                })}
                /* A track that will not load stops the music rather than
                   silently pretending to play. Advancing to the next one would
                   be the sensible-looking behaviour and the wrong one: if the
                   files are missing, it would walk the whole shelf in a second
                   and land on nothing. */
                onError={() => setPlaying(false)}
            />
        </JukeboxContext.Provider>
    );
}

/**
 * The jukebox, or nothing.
 *
 * Answers `null` outside a provider rather than throwing, which is the opposite
 * of `useLocale`'s rule and deliberate: a screen without a locale cannot render
 * its own copy, where a header without a jukebox simply draws one control fewer.
 * That is what keeps every existing component test — none of which knows this
 * exists — mounting without a new wrapper.
 */
export function useJukebox() {
    return useContext(JukeboxContext);
}
